/**
 * Vertical Instagram collage.
 *
 * The Instafeed app holds the Instagram token and renders its own feed into a
 * `#insta-feed` element. We keep that element on the page but visually hidden,
 * then lift the image URLs out of it and rebuild them as our own vertical
 * scrolling columns.
 *
 * This reads Instafeed's internal markup, which is not a public API. If the app
 * changes its DOM, extraction returns nothing and the section silently keeps the
 * photos the merchant set in the theme editor. It never renders empty.
 *
 * The script also owns how many columns are shown. CSS alone can't do it: the
 * column count and the number of column elements have to change together, or
 * the surplus columns wrap onto a second row and fall outside the box.
 */

const SETTLE_TIMEOUT_MS = 12000;
const POLL_INTERVAL_MS = 250;
const PLACEHOLDER_MARKER = 'placeholder.png';
const MOBILE_QUERY = '(max-width: 749px)';
const MOBILE_MAX_COLUMNS = 2;
const RESIZE_DEBOUNCE_MS = 200;

/**
 * Pull image URL + post link out of whatever Instafeed rendered.
 * Tried most-stable first.
 */
function extractPosts(source) {
  const posts = [];
  const seen = new Set();

  const add = (url, link) => {
    if (!url || seen.has(url) || url.includes(PLACEHOLDER_MARKER)) return;
    seen.add(url);
    posts.push({ url, link: link || '' });
  };

  const linkFor = (node) => {
    const anchor = node.closest('a');
    return anchor && anchor.href && !anchor.href.endsWith('#') ? anchor.href : '';
  };

  // Preferred: the app stamps the full-size URL onto each item as an attribute,
  // so we don't have to wait for its lazy-loader to swap any `src` in.
  source.querySelectorAll('[data-instafeed-thumbnail]').forEach((node) => {
    add(node.getAttribute('data-instafeed-thumbnail'), linkFor(node));
  });

  if (posts.length === 0) {
    source.querySelectorAll('img[data-instafeed-src]').forEach((node) => {
      add(node.getAttribute('data-instafeed-src'), linkFor(node));
    });
  }

  if (posts.length === 0) {
    source.querySelectorAll('img[src]').forEach((node) => {
      add(node.getAttribute('src'), linkFor(node));
    });
  }

  return posts;
}

/**
 * Instafeed renders asynchronously and gives no "done" callback we can rely on,
 * so poll until the post count stops growing, then settle.
 */
function waitForPosts(source) {
  return new Promise((resolve) => {
    const startedAt = Date.now();
    let lastCount = 0;
    let stableTicks = 0;

    const tick = () => {
      const posts = extractPosts(source);

      if (posts.length > 0 && posts.length === lastCount) {
        stableTicks += 1;
        if (stableTicks >= 2) {
          resolve(posts);
          return;
        }
      } else {
        stableTicks = 0;
      }

      lastCount = posts.length;

      if (Date.now() - startedAt > SETTLE_TIMEOUT_MS) {
        resolve(posts);
        return;
      }

      setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
  });
}

function buildItem(post, altText) {
  const item = document.createElement('div');
  item.className = 'ig-collage__item';

  const image = document.createElement('img');
  image.className = 'ig-collage__image';
  image.src = post.url;
  image.loading = 'lazy';
  image.decoding = 'async';
  image.alt = altText;

  if (post.link) {
    const anchor = document.createElement('a');
    anchor.className = 'ig-collage__link';
    anchor.href = post.link;
    anchor.target = '_blank';
    anchor.rel = 'noopener';
    anchor.appendChild(image);
    item.appendChild(anchor);
  } else {
    item.appendChild(image);
  }

  return item;
}

/** The second copy is decoration for the loop — keep it out of the a11y tree. */
function asDuplicate(node) {
  const copy = node.cloneNode(true);
  copy.setAttribute('aria-hidden', 'true');

  const anchor = copy.querySelector('a');
  if (anchor) anchor.tabIndex = -1;

  const image = copy.querySelector('img');
  if (image) image.alt = '';

  return copy;
}

class InstagramCollage extends HTMLElement {
  connectedCallback() {
    this.tracks = Array.from(this.querySelectorAll('.ig-collage__track'));
    if (this.tracks.length === 0) return;

    this.items = this.captureRenderedItems();
    this.visibleColumns = 0;

    this.layout();
    this.watchViewport();

    if (this.dataset.source === 'instagram') this.hydrate();
  }

  disconnectedCallback() {
    if (this.mediaQuery && this.onViewportChange) {
      this.mediaQuery.removeEventListener('change', this.onViewportChange);
    }
    clearTimeout(this.resizeTimer);
  }

  /**
   * The photos Liquid already rendered, minus the duplicate pass. These are the
   * fallback set and stay in play if the feed never arrives.
   */
  captureRenderedItems() {
    const items = [];
    this.tracks.forEach((track) => {
      track.querySelectorAll(':scope > .ig-collage__item:not([aria-hidden="true"])').forEach((node) => {
        items.push(node.cloneNode(true));
      });
    });
    return items;
  }

  columnsForViewport() {
    const total = this.tracks.length;
    if (!window.matchMedia(MOBILE_QUERY).matches) return total;
    return Math.min(total, MOBILE_MAX_COLUMNS);
  }

  watchViewport() {
    this.mediaQuery = window.matchMedia(MOBILE_QUERY);
    this.onViewportChange = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.layout(), RESIZE_DEBOUNCE_MS);
    };
    this.mediaQuery.addEventListener('change', this.onViewportChange);
  }

  layout({ force = false } = {}) {
    const columns = this.columnsForViewport();
    if (!force && columns === this.visibleColumns) return;

    this.visibleColumns = columns;
    this.style.setProperty('--ig-collage-visible', String(columns));

    this.tracks.forEach((track, index) => {
      track.parentElement.classList.toggle('ig-collage__column--hidden', index >= columns);
    });

    if (this.items.length === 0) return;

    for (let index = 0; index < columns; index += 1) {
      const columnItems = this.items.filter((_, position) => position % columns === index);
      const fragment = document.createDocumentFragment();

      // Rendered twice so the loop point is invisible, matching the Liquid.
      columnItems.forEach((node) => fragment.appendChild(node.cloneNode(true)));
      columnItems.forEach((node) => fragment.appendChild(asDuplicate(node)));

      this.tracks[index].replaceChildren(fragment);
    }
  }

  async hydrate() {
    const source = this.querySelector('#insta-feed');
    if (!source) return;

    const posts = await waitForPosts(source);

    // Nothing usable — leave the editor-set photos exactly as they are.
    if (posts.length === 0) {
      this.dataset.feedState = 'fallback';
      return;
    }

    const altText = this.dataset.altPrefix || 'Instagram post';
    this.items = posts.map((post) => buildItem(post, altText));
    this.layout({ force: true });
    this.dataset.feedState = 'live';
  }
}

if (!customElements.get('instagram-collage')) {
  customElements.define('instagram-collage', InstagramCollage);
}

// The theme editor re-renders sections in place; Instafeed re-runs on the same
// event, so we re-read it afterwards.
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('instagram-collage').forEach((node) => {
    if (node.dataset.source === 'instagram') node.hydrate();
  });
});
